import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import '../Shared/Menu.css';
import './Login.css';
import logo from '../../images/audithippo.png';
import usePageTitle from '../Shared/usePageTitle';

function Login() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    usePageTitle('Login');

    return (
        <>
            {/* Mobile Menu Overlay */}
            <div
                className={`mobile-menu-overlay${menuOpen ? ' show' : ''}`}
                onClick={() => setMenuOpen(false)}
            />

            {/* Mobile Slide Menu */}
            <div className={`mobile-menu${menuOpen ? ' show' : ''}`}>
                <div className="mobile-menu-header">
                    <a href="#" className="logobock d-flex align-items-center gap-2">
                        <div className="hippolog">
                            <img src={logo} alt="AuditHippo" />
                        </div>
                        <span className="brand-name">
                            Audit<span className="hippotxt">Hippo</span>
                        </span>
                    </a>
                    <div
                        className="mobile-menu-close"
                        onClick={() => setMenuOpen(false)}
                        role="button"
                        aria-label="Close menu"
                    >
                        ✕
                    </div>
                </div>
            </div>

            {/* Navbar */}
            <nav className="navbar-custom">
                <div className="container-custom">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <button
                                className="mobile-menu-btn"
                                onClick={() => setMenuOpen(true)}
                                aria-label="Open menu"
                            >
                                <span />
                                <span />
                                <span />
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <a href="#" className="logobock d-flex align-items-center gap-2">
                                    <div className="hippolog">
                                        <img src={logo} alt="AuditHippo" />
                                    </div>
                                    <span className="brand-name">
                                        Audit<span className="hippotxt">Hippo</span>
                                    </span>
                                </a>
                                <span className="divider-mobile" />
                                <span className="marketing-cloud-text">Marketing Cloud</span>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="container-main fade-up" style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div className="page-header mb-4">
                    <h1>Login</h1>
                    <p>Secure access for AuditHippo users</p>
                </div>

                <div
                    className="login-screens-row"
                    style={{ display: 'flex', justifyContent: 'center' }}
                >
                    <div style={{ width: '100%', maxWidth: 520 }}>
                        <p className="login-screen-label">User Login</p>

                        <div className="login-card">
                            <div className="card-body">
                                {/* Brand */}
                                <div className="d-flex align-items-center gap-2 mb-4">
                                    <div className="logo-box">AH</div>
                                    <span className="brand-name">AuditHippo</span>
                                </div>

                                <span className="login-badge-user">User Access</span>

                                <h2 className="login-card-title">Welcome back</h2>
                                <p className="login-card-subtitle">
                                    Sign in to your AuditHippo account to continue.
                                </p>

                                {/* Login Form */}
                                <LoginForm onLogin={() => navigate('/automations')} />

                                <p className="login-footer-text mb-0">
                                    Do not have an account?<br />
                                    Contact your{' '}
                                    <a href="https://www.bluehippo.io/contact.html" target="_blank" rel="noopener noreferrer" className="login-link">AuditHippo Admin</a>{' '}
                                    for an invite.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

const LOGIN_URL = '/rest/auth/v1/login';

function LoginForm({ onLogin }) {
    const [email, setEmail]         = useState('');
    const [password, setPassword]   = useState('');
    const [error, setError]         = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [debugInfo, setDebugInfo]   = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Please enter your email and password.');
            return;
        }

        const requestBody = { email, password };
        const requestHeaders = { 'Content-Type': 'application/json' };

        setDebugInfo({
            request: { url: LOGIN_URL, method: 'POST', headers: requestHeaders, body: requestBody },
            response: null,
        });

        setSubmitting(true);
        try {
            const res = await axios.post(LOGIN_URL, requestBody, { headers: requestHeaders });

            const data = res.data;
            setDebugInfo(prev => ({
                ...prev,
                response: { status: res.status, statusText: res.statusText, data },
            }));
            localStorage.setItem('isLoggedIn', 'true');
            if (data && data.token) localStorage.setItem('userAuthToken', data.token);
            if (data && data.sessionId) localStorage.setItem('userSessionId', data.sessionId);
            if (data && data.connectionId) localStorage.setItem('userConnectionId', data.connectionId);
            if (data && data.connectionName) localStorage.setItem('userConnectionName', data.connectionName);
            if (data && data.connectionSyncStatus) localStorage.setItem('userConnectionSyncStatus', data.connectionSyncStatus);
            onLogin();
        } catch (err) {
            const data = err.response?.data;
            setDebugInfo(prev => ({
                ...prev,
                response: {
                    status: err.response?.status || 'Network Error',
                    statusText: err.response?.statusText || err.message,
                    data,
                },
            }));
            const msg = (data && (data.message || data.error)) || 'Unable to reach the server. Please try again.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
                <label htmlFor="userEmail" className="login-label">Email</label>
                <input
                    type="email"
                    className="login-input"
                    id="userEmail"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    autoComplete="email"
                    disabled={submitting}
                />
            </div>

            <div className="mb-2">
                <label htmlFor="userPassword" className="login-label">Password</label>
                <input
                    type="password"
                    className="login-input"
                    id="userPassword"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    autoComplete="current-password"
                    disabled={submitting}
                />
            </div>

            <div className="d-flex justify-content-end mb-3">
                <Link to="/forgot-password" className="login-link">Forgot password?</Link>
            </div>

            {error && (
                <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
            )}

            <button type="submit" className="login-primary-btn" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign in'}
            </button>
        </form>
    );
}

export default Login;
